import { useState, useCallback } from 'react'
import { api } from '../../api/client'
import type { Project } from '../../api/client'
import { useToast } from '../../utils/toast'

interface ProjectsPanelProps {
  projects: Project[]
  onClose: () => void
  onProjectsChanged: (projects: Project[]) => void
}

// Sentinel selection id for "creating a new project" (vs. an existing project id).
const NEW = '__new__'

/**
 * Manage Projects — master/detail editor.
 *
 * Left rail picks a project (or "New project"); the right pane edits it in a
 * spacious form whose setup-prompt field fills the available height. Editing is
 * deliberately NOT inline in the list: the setup prompt is the heart of a project
 * and needs real estate for a rich editing experience (and, in future, syntax
 * checking / AI authoring / a toolbar) — a two-line inline box doesn't scale.
 */
export function ProjectsPanel({ projects, onClose, onProjectsChanged }: ProjectsPanelProps) {
  const { addToast } = useToast()
  // selectedId: an existing project id, NEW (create mode), or null (nothing picked).
  // Default to the first project so the editor is immediately useful.
  const [selectedId, setSelectedId] = useState<string | null>(projects[0]?.id ?? null)
  const [draftName, setDraftName] = useState(projects[0]?.name ?? '')
  const [draftCorePrompt, setDraftCorePrompt] = useState(projects[0]?.core_prompt ?? '')
  const [saving, setSaving] = useState(false)

  const selected = selectedId && selectedId !== NEW ? projects.find(p => p.id === selectedId) ?? null : null
  const isNew = selectedId === NEW

  // Whether the draft differs from the persisted project (or has content in new mode).
  const dirty = isNew
    ? draftName.trim() !== '' || draftCorePrompt !== ''
    : !!selected && (draftName !== selected.name || draftCorePrompt !== (selected.core_prompt ?? ''))

  // Switch selection, guarding against silently discarding unsaved edits.
  const select = useCallback((id: string | null) => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return
    if (id === NEW) {
      setSelectedId(NEW)
      setDraftName('')
      setDraftCorePrompt('')
      return
    }
    const p = id ? projects.find(x => x.id === id) ?? null : null
    setSelectedId(p?.id ?? null)
    setDraftName(p?.name ?? '')
    setDraftCorePrompt(p?.core_prompt ?? '')
  }, [dirty, projects])

  const handleSave = useCallback(async () => {
    if (saving || !draftName.trim()) return
    setSaving(true)
    try {
      if (isNew) {
        const p = await api.createProject({
          name: draftName.trim(),
          core_prompt: draftCorePrompt.trim() || undefined,
        })
        onProjectsChanged([p, ...projects])
        setSelectedId(p.id)
        addToast(`Project "${p.name}" created.`, 'success')
      } else if (selected) {
        await api.updateProject(selected.id, {
          name: draftName.trim() || undefined,
          core_prompt: draftCorePrompt || null,
        })
        onProjectsChanged(projects.map(p =>
          p.id === selected.id
            ? { ...p, name: draftName.trim() || p.name, core_prompt: draftCorePrompt || null }
            : p,
        ))
        addToast('Project updated.', 'success')
      }
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Save failed.', 'error')
    } finally {
      setSaving(false)
    }
  }, [saving, draftName, draftCorePrompt, isNew, selected, projects, onProjectsChanged, addToast])

  const handleDelete = useCallback(async () => {
    if (!selected) return
    if (!window.confirm(`Delete project "${selected.name}"? Conversations will become project-less (not deleted).`)) return
    try {
      await api.deleteProject(selected.id)
      const remaining = projects.filter(x => x.id !== selected.id)
      onProjectsChanged(remaining)
      // Move selection to the next available project (or empty state).
      setSelectedId(remaining[0]?.id ?? null)
      setDraftName(remaining[0]?.name ?? '')
      setDraftCorePrompt(remaining[0]?.core_prompt ?? '')
      addToast(`Project "${selected.name}" deleted.`, 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Delete failed.', 'error')
    }
  }, [selected, projects, onProjectsChanged, addToast])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Manage Projects</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        </div>

        {/* Body: picker (left) + editor (right) */}
        <div className="flex flex-1 min-h-0">
          {/* Left rail — project picker (selection only, never inline editing). */}
          <div className="w-64 shrink-0 border-r border-gray-200 flex flex-col">
            <div className="p-2 border-b border-gray-100">
              <button
                onClick={() => select(NEW)}
                className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md transition-colors ${
                  isNew ? 'bg-indigo-600 text-white' : 'text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                + New project
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {projects.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No projects yet.</p>
              ) : (
                projects.map(p => {
                  const active = p.id === selectedId
                  return (
                    <button
                      key={p.id}
                      onClick={() => select(p.id)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                        active ? 'bg-indigo-50 border border-indigo-200' : 'border border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <p className={`text-sm font-medium truncate ${active ? 'text-indigo-700' : 'text-gray-800'}`}>
                        {p.name}
                      </p>
                      {p.core_prompt && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{p.core_prompt}</p>
                      )}
                    </button>
                  )
                })
              )}
            </div>
          </div>

          {/* Right pane — spacious editor for the selected/new project. */}
          {!isNew && !selected ? (
            <div className="flex-1 flex items-center justify-center text-center px-8">
              <div>
                <p className="text-sm font-medium text-gray-400">Select a project to edit</p>
                <p className="text-xs text-gray-300 mt-1">or create a new one to get started</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-w-0 p-5 gap-4">
              {/* Project name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Project name</label>
                <input
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  autoFocus={isNew}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                  placeholder="e.g. Q3 Roof Inspections"
                />
              </div>

              {/* Setup prompt — fills the remaining height for a rich editing experience. */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex items-end justify-between mb-1">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Setup prompt</label>
                    <p className="text-xs text-gray-400">Prepended to every chat in this project.</p>
                  </div>
                  {/* Room for future tooling (syntax check, AI authoring) lives here. */}
                  <span className="text-xs text-gray-400 tabular-nums">{draftCorePrompt.length} chars</span>
                </div>
                <textarea
                  value={draftCorePrompt}
                  onChange={e => setDraftCorePrompt(e.target.value)}
                  className="flex-1 min-h-[240px] w-full resize-none font-mono text-sm leading-relaxed text-gray-900 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                  placeholder="Describe the project's context, goals, tone, and any standing instructions the agent should always follow for chats in this project…"
                  spellCheck={false}
                />
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-between shrink-0">
                <div>
                  {selected && (
                    <button
                      onClick={() => { void handleDelete() }}
                      className="text-sm text-red-600 hover:text-red-700 px-2 py-1.5 rounded hover:bg-red-50"
                    >
                      Delete project
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
                  <button
                    onClick={() => { void handleSave() }}
                    disabled={saving || !draftName.trim() || !dirty}
                    className="text-sm px-4 py-1.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving…' : isNew ? 'Create project' : 'Save changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
